/**
 * GS1 Application Identifier (AI) parser.
 *
 * GS1 DataMatrix / QR codes on supplier deliveries encode product info using
 * Application Identifiers — short numeric prefixes followed by a value. The
 * separator between variable-length AIs is the FNC1 character (Unicode
 * 0x1D, GS — Group Separator), or the explicit prefix `]d2` / `]Q3` /
 * `]C1` (which some scanners include to flag a GS1-conforming code).
 *
 * Examples:
 *   ]d201095012345678903 17260520 10 ABC123-77 30 12
 *     → GTIN: 09501234567890 (AI 01)
 *     → Expiry: 2026-05-20    (AI 17)
 *     → Batch: ABC123-77      (AI 10)
 *     → Qty: 12               (AI 30)
 *
 * We only parse the AIs that map to fields we collect on the Receival form
 * (batch, use-by, GTIN for product hinting, quantity). Everything else is
 * preserved in `rawAIs` for diagnostics. The parser is intentionally lax —
 * it never throws on malformed input, it just returns whatever it can read
 * so a partial scan still fills the form rather than aborting.
 */

const FNC1 = "\u001D"; // ASCII 0x1D group separator

/** Fixed-length AIs that don't need an FNC1 terminator. */
const FIXED_LENGTH_AI = {
  "00": 18,   // SSCC
  "01": 14,   // GTIN
  "02": 14,   // GTIN of contained trade item
  "11": 6,    // Production date YYMMDD
  "12": 6,    // Due date YYMMDD
  "13": 6,    // Packaging date YYMMDD
  "15": 6,    // Best-before date YYMMDD
  "16": 6,    // Sell-by date YYMMDD
  "17": 6,    // Expiry date YYMMDD
  "20": 2,    // Variant
  "31": 6,    // Net weight kg + 3 digits (full AI is 4 chars, value is 6)
  "32": 6,    // Net weight lb
  "33": 6,    // Logistic weight kg
  "34": 6,    // Length
  "35": 6,    // Width
  "36": 6,    // Depth
  "41": 13,   // Customer PO
};

/** Convert a YYMMDD GS1 date into ISO YYYY-MM-DD. Two-digit years > 50 are
 *  assumed 1900-prefix (per GS1 General Spec §7.12), else 2000-prefix.
 *  Day = 00 means "end of month" per GS1 — we approximate as the last day. */
function decodeGs1Date(yymmdd) {
  if (!/^\d{6}$/.test(yymmdd)) return "";
  const yy = parseInt(yymmdd.slice(0, 2), 10);
  const mm = parseInt(yymmdd.slice(2, 4), 10);
  let dd = parseInt(yymmdd.slice(4, 6), 10);
  const year = yy >= 51 ? 1900 + yy : 2000 + yy;
  if (mm < 1 || mm > 12) return "";
  if (dd === 0) {
    // last day of month
    dd = new Date(year, mm, 0).getDate();
  }
  if (dd < 1 || dd > 31) return "";
  const m = String(mm).padStart(2, "0");
  const d = String(dd).padStart(2, "0");
  return `${year}-${m}-${d}`;
}

/** True iff the raw code looks like a GS1-encoded payload — either has the
 *  GS1 symbology-identifier prefix, contains the FNC1 separator, or
 *  starts with an `(AI)` annotated form. */
export function looksLikeGs1(raw) {
  if (typeof raw !== "string" || !raw) return false;
  if (raw.startsWith("]d2") || raw.startsWith("]Q3") || raw.startsWith("]C1") || raw.startsWith("]e0")) {
    return true;
  }
  if (raw.includes(FNC1)) return true;
  // Human-readable annotated form: (01)09501234567890(17)260520(10)ABC
  if (/^\(\d{2,4}\)/.test(raw)) return true;
  return false;
}

/** Strip the symbology identifier (]d2 / ]Q3 / ]C1 / ]e0) prefix. */
function stripSymbology(raw) {
  if (typeof raw !== "string") return "";
  if (raw.length >= 3 && raw[0] === "]") return raw.slice(3);
  return raw;
}

/** Parse a GS1 payload into a record of AI → string value plus a few
 *  pre-decoded convenience fields. Returns null for non-GS1 input. */
export function parseGs1(raw) {
  if (!looksLikeGs1(raw)) return null;

  // First handle the human-readable "(AI)value(AI)value" form some scanners emit.
  if (/^\(\d{2,4}\)/.test(raw)) {
    const ais = {};
    const re = /\((\d{2,4})\)([^(]+)/g;
    let m;
    while ((m = re.exec(raw)) !== null) {
      ais[m[1]] = m[2].trim();
    }
    return assembleResult(ais, raw);
  }

  const payload = stripSymbology(raw);
  const ais = {};
  let i = 0;
  while (i < payload.length) {
    // AIs are 2-4 digits — try longest match first.
    let aiLen = 2;
    let ai = payload.slice(i, i + 2);
    if (!/^\d{2}$/.test(ai)) break;
    // Some AIs are 3 (eg 240, 241, 250, 251, 392, 393, 703) or 4 digits.
    // Fixed-length lookup uses just the 2-char prefix; for variable-length
    // AIs we extend the prefix as needed by GS1 spec ranges below.
    if (["24", "25", "39", "70", "71", "72", "80"].includes(ai)) {
      aiLen = 3;
      ai = payload.slice(i, i + 3);
    }
    i += aiLen;
    const fixed = FIXED_LENGTH_AI[ai] || FIXED_LENGTH_AI[ai.slice(0, 2)];
    let value;
    if (fixed !== undefined) {
      value = payload.slice(i, i + fixed);
      i += fixed;
    } else {
      // Variable-length — read until FNC1 or end of string.
      const next = payload.indexOf(FNC1, i);
      if (next === -1) {
        value = payload.slice(i);
        i = payload.length;
      } else {
        value = payload.slice(i, next);
        i = next + 1;
      }
    }
    ais[ai] = value;
  }
  return assembleResult(ais, raw);
}

function assembleResult(ais, raw) {
  const gtin = ais["01"] || ais["02"] || "";
  const lot = ais["10"] || "";
  const serial = ais["21"] || "";
  // Prefer hard expiry (17) > best-before (15) > sell-by (16).
  const expiryRaw = ais["17"] || ais["15"] || ais["16"] || "";
  const useBy = expiryRaw ? decodeGs1Date(expiryRaw) : "";
  const productionDate = ais["11"] ? decodeGs1Date(ais["11"]) : "";
  // Variable-count AI 30 (item count). We also pluck a net weight if present
  // for the quantity hint.
  const qty = ais["30"] || ais["37"] || ais["310"] || ais["320"] || ais["330"] || "";
  return {
    gtin,
    batch: lot,
    serial,
    use_by: useBy,
    use_by_raw: expiryRaw,
    production_date: productionDate,
    quantity: qty,
    rawAIs: ais,
    raw,
  };
}
